import type { IconName } from '@customerio/pluma-icons';
import type { EnvironmentsAttributeDetailResponse, EnvironmentsAttributeListItem } from '@customerio/hydra-ui-api';

/**
 * The four schema browsers the data index offers, as they appear in the URL
 * (`/data-index/:type`). Person attributes and events are workspace-wide;
 * object and relationship attributes are scoped to one object type.
 */
export const DATA_INDEX_TYPES = [
  'attributes',
  'events',
  'object-attributes',
  'object-relationship-attributes',
] as const;

export type DataIndexType = (typeof DATA_INDEX_TYPES)[number];

/** The browser a URL segment names, or null when it names none of them. */
export function asDataIndexType(value: string) {
  return DATA_INDEX_TYPES.find((type) => type === value) ?? null;
}

/**
 * The kind of a single row, which is what everything below the list works in:
 * the singular of `DataIndexType`, and the discriminator that decides an
 * item's activity-log type, icon, labels, and which usage surfaces it has.
 */
export const DATA_INDEX_ITEM_KINDS = [
  'attribute',
  'event',
  'object-attribute',
  'object-relationship-attribute',
] as const;

export type DataIndexItemKind = (typeof DATA_INDEX_ITEM_KINDS)[number];

/** The activity-log `type` each kind's changes are recorded under. */
export const DATA_INDEX_ITEM_ACTIVITY_LOGS = {
  attribute: 'attribute_change',
  event: 'event',
  'object-attribute': 'object_attribute_change',
  'object-relationship-attribute': 'object_relationship_attribute_change',
} satisfies Record<DataIndexItemKind, string>;

export const DATA_INDEX_ITEM_ICONS = {
  attribute: 'person',
  event: 'trigger-event',
  'object-attribute': 'object',
  'object-relationship-attribute': 'object',
} satisfies Record<DataIndexItemKind, IconName>;

/**
 * Where an attribute lives, as the backend numbers it in `attribute_type`.
 * The number alone does not settle the answer — an object or relationship
 * attribute also needs an object type; see `dataIndexItemKindOf`.
 */
export const DataIndexAttributeType = {
  Person: 1,
  Object: 2,
  Relationship: 3,
} as const;

/**
 * The object type id that means "no object type", which is how the backend
 * spells a person attribute.
 */
export const FALLBACK_OBJECT_TYPE_ID = 0;

/** The fields that decide which kind of attribute a row is. */
export type DataIndexAttributeScope = Pick<EnvironmentsAttributeListItem, 'attribute_type' | 'object_type_id'>;

/**
 * Which kind of attribute a row is. An attribute claiming to be an object or
 * relationship attribute without an object type is a person attribute: the
 * pair has to agree, or the item's activity log and usage surfaces would be
 * addressed against an object type that does not exist.
 */
export function dataIndexItemKindOf({ attribute_type, object_type_id }: DataIndexAttributeScope): DataIndexItemKind {
  if ((object_type_id ?? FALLBACK_OBJECT_TYPE_ID) === FALLBACK_OBJECT_TYPE_ID) {
    return 'attribute';
  }
  if (attribute_type === DataIndexAttributeType.Object) {
    return 'object-attribute';
  }
  if (attribute_type === DataIndexAttributeType.Relationship) {
    return 'object-relationship-attribute';
  }
  return 'attribute';
}

/**
 * What a data-index item is used by. Attribute details carry all seven
 * collections; event details carry only the first three, and the rest are
 * absent rather than empty — which is why every one is optional here.
 */
export type DataIndexUsage = Partial<
  Pick<
    EnvironmentsAttributeDetailResponse,
    'segments' | 'campaigns' | 'newsletters' | 'templates' | 'snippets' | 'layouts' | 'design_studio'
  >
>;

export type DataIndexUsageKey = keyof DataIndexUsage;

/**
 * The surface a data-index action was taken from. Every action is reachable
 * from more than one place, so the analytics event says which one.
 */
export const DataIndexLocation = {
  Table: 'table',
  Drawer: 'drawer',
  DetailsPage: 'details_page',
} as const;

export type DataIndexLocationValue = (typeof DataIndexLocation)[keyof typeof DataIndexLocation];

/**
 * What the analytics events call an item. Object and relationship attributes
 * report as attributes: the analysis splits by surface, not by attribute
 * flavour.
 */
export type DataIndexAnalyticsType = 'attribute' | 'event';
